import "./CreateInvoiceModal.css";
import "./DiscountType.css";
import {
  getCreateInvoicePopupMetaDataValues,
  initialDiscountState,
} from "./ViewOrderUtils";
import { useState, useEffect, KeyboardEvent } from "react";
import { CreateInvoiceModalProps, DiscountDetails } from "./ViewOrderType";
import Switch from "react-switch";
import DiscountType from "./DiscountType";
import { truncateToTwoDecimals } from "../../components/dms_common/DmsUtils";
import useShortcutKeys from "../../components/dms_common/ShortcutKeys";
import BigDecimal from "js-big-decimal";
import { calculateTotalAmountAndQuantities } from "../../components/dms_common/CartSummaryFooter";


interface DistributorDiscountSelectProps {
  cartData: any;
  discount: DiscountDetails;
  setDiscount: React.Dispatch<React.SetStateAction<DiscountDetails>>;
  hideAdditionalDistributorDiscount: boolean;
  showBothHeaderLevelDiscountedAmountAndPercentage?: boolean;
}


const DistributorDiscountSelect: React.FC<DistributorDiscountSelectProps> = ({
  cartData,
  discount,
  setDiscount,
  hideAdditionalDistributorDiscount = false,
  showBothHeaderLevelDiscountedAmountAndPercentage,
}) => {
  const markDiscountType = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user?.markDiscountType;
  const [discountAmount, setDiscountAmount] = useState<string>("0");
  const [discountPercentage, setDiscountPercentage] = useState<string>("0");
  const [finalAmount, setFinalAmount] = useState<number>(0);
  const [totalData, setTotalData] = useState<any>({});

  useEffect(() => {
    const run = async () => {

      const hundred = new BigDecimal(100);
      const one = new BigDecimal(1);

      const totalData = calculateTotalAmountAndQuantities(
        Object.values(cartData ?? {})
      );

      setTotalData(totalData);

      const orderNetAmount = new BigDecimal(totalData?.totalAmount);
      const inputValue = new BigDecimal(truncateToTwoDecimals(Number(discount?.discountValue || "0")));
      let discountValue = new BigDecimal(0);
      let discountPercent = new BigDecimal(0);
        if(!discount.discountType || discount?.discountValue?.length === 0) {
          setFinalAmount(Number(truncateToTwoDecimals(Number(totalData?.totalAmount || 0))));
          return;
        }
        if(discount?.discountValue === "0") {
          setDiscountAmount("0");
          setDiscountPercentage("0");
          setFinalAmount(Number(truncateToTwoDecimals(Number(totalData?.totalAmount || 0))));
          return;
        }

      if (markDiscountType) {
        if ("markup" === markDiscountType?.toLowerCase()) {
          if (discount?.discountType === "VALUE") {
            // Given: Value, Calculate: Percentage (Reverse Markup)
            // Formula: percentage = ((netAmount / (netAmount - value)) - 1) * 100
            discountValue = inputValue;
            if (parseFloat(orderNetAmount.getValue()) > 0) {
              const denominator = orderNetAmount.subtract(inputValue);
              discountPercent = orderNetAmount.divide(denominator).subtract(one).multiply(hundred);
            } else {
              discountPercent = new BigDecimal(0);
            }
            setDiscountAmount(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountValue.getValue());
            setDiscountPercentage(discountPercent.getValue());
          } else {
            // Given: Percentage, Calculate: Value (Markup)
            // Formula: value = netAmount - (netAmount / (1 + percentage/100))
            discountPercent = inputValue;
            const divisor = one.add(inputValue.divide(hundred));
            discountValue = orderNetAmount.subtract(orderNetAmount.divide(divisor));
            setDiscountAmount(discountValue.getValue());
            setDiscountPercentage(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountPercent.getValue());
          }
        } else { // markdown
          if (discount?.discountType === "VALUE") {
            // Given: Value, Calculate: Percentage (Reverse Standard)
            // Formula: percentage = (value / netAmount) * 100
            discountValue = inputValue;
            if (parseFloat(orderNetAmount.getValue()) > 0) {
              discountPercent = inputValue.divide(orderNetAmount).multiply(hundred);
            } else {
              discountPercent = new BigDecimal(0);
            }
            setDiscountAmount(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountValue.getValue());
            setDiscountPercentage(discountPercent.getValue());
          } else {
            // Given: Percentage, Calculate: Value (Standard)
            // Formula: value = netAmount * (percentage / 100)
            discountPercent = inputValue;
            discountValue = inputValue.divide(hundred).multiply(orderNetAmount);
            setDiscountAmount(discountValue.getValue());
            setDiscountPercentage(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountPercent.getValue());
          }
        }
      } else {
        // No mark discount (default behavior - standard calculation)
        if (discount?.discountType === "VALUE") {
          // Given: Value, Calculate: Percentage (Reverse Standard)
          discountValue = inputValue;
          if (parseFloat(orderNetAmount.getValue()) > 0) {
            discountPercent = inputValue.divide(orderNetAmount).multiply(hundred);
          } else {
            discountPercent = new BigDecimal(0);
          }
          setDiscountAmount(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountValue.getValue());
          setDiscountPercentage(discountPercent.getValue());
        } else {
          // Given: Percentage, Calculate: Value (Standard)
          discountPercent = inputValue;
          discountValue = inputValue.divide(hundred).multiply(orderNetAmount);
          setDiscountAmount(discountValue.getValue());
          setDiscountPercentage(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountPercent.getValue());
        }
      }

      const finalAmount = orderNetAmount.subtract(discountValue);
      setFinalAmount(Number(truncateToTwoDecimals(Number(finalAmount.getValue()))));
    }
    run()
  }, [cartData, discount, markDiscountType]);

  const handleSwitchChange = (checked: boolean) => {
    if (!checked) {
      setDiscount(initialDiscountState);
    } else {
      setDiscount((prevDiscount) => ({
        ...prevDiscount,
        discountApplied: checked,
      }));
    }
  };

  const handleRadioToggle = (value: "VALUE" | "PERCENTAGE") => {
    setDiscount((prevDiscount) => ({
      ...prevDiscount,
      discountValue: "0",
      discountType: value,
    }));
  };

  const handleDiscountValueChange = (value: string) => {
    setDiscount((prevDiscount) => ({
      ...prevDiscount,
      discountValue: value,
    }));
  };

  const handleResetChanges = () => {
    setDiscount({ ...initialDiscountState, discountApplied: true });
  };

  const reset = () => {
    setDiscount(initialDiscountState);
    setFinalAmount(0);
  };

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const designationBasedUomSecondary = ConfigDictionary.designationBasedUomSecondary;

  const designationEnabled  =  designationBasedUomSecondary?.enable ?? false;
  const availableDesignations = designationBasedUomSecondary?.roles?.designationKeys?.map(((item: { type: any; }) => item?.type)) || [];
  const currentDesignation = JSON.parse(localStorage.authContext).user.designation || [];
  const designation = availableDesignations.find((type: any) => 
    currentDesignation.includes(type)
  );
  const designationKeys = designationBasedUomSecondary?.roles?.designationKeys ?? [];
  const matchedRole = designationKeys.find((role: { type: string; uomAllowed: any }) => role.type === designation);

    const handleSwitchKeyDown = (event: KeyboardEvent) => {
    // Check if the pressed key is 'Enter' or ' ' (Space)
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault(); // Prevent default action (like form submission or scrolling)
      handleSwitchChange(!discount?.discountApplied); // Toggle the switch state
    }
  };

    useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "a") {
        event.preventDefault();
      } else if (event.key === "Escape") {
        event.preventDefault();
        reset();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const { positiveCta } = useShortcutKeys();

  if ( hideAdditionalDistributorDiscount || !discount?.discountApplied ){
    return null;
  }

  return (
    <div>
      <div className="add-discount-container" style={{backgroundColor: "transparent"}}>
        <div className="discount-configure-container"
          onClick={(e) => e.stopPropagation()}
        >
          <DiscountType
            type="Value"
            handleRadioToggle={() => handleRadioToggle("VALUE")}
            discount={discount}
            handleDiscountValueChange={handleDiscountValueChange}
            maxVal={totalData?.totalAmount || 0}
            discountAmount={discountAmount}
            discountPercentage={discountPercentage}
            showBothHeaderLevelDiscountedAmountAndPercentage={showBothHeaderLevelDiscountedAmountAndPercentage}
          />
          <DiscountType
            type="Percentage"
            handleRadioToggle={() => handleRadioToggle("PERCENTAGE")}
            discount={discount}
            labelSuffix="%"
            handleDiscountValueChange={handleDiscountValueChange}
            maxVal={100}
            discountAmount={discountAmount}
            discountPercentage={discountPercentage}
            showBothHeaderLevelDiscountedAmountAndPercentage={showBothHeaderLevelDiscountedAmountAndPercentage}
          />
        </div>

        <div className="final-amount-container" style={{width: "100%", justifyContent: "start", paddingLeft: "10px"}}>
          <p>
            Final amount (after additional discount):{" "}
            <span>
              {ConfigDictionary.currency}
              {finalAmount}
            </span>
          </p>
        </div>
      </div>

      <div
        className="createInvoice-footerContainer"
        style={{
          justifyContent: "flex-end",
          marginTop: "0px",
          marginRight: "10px",
        }}
      >
        <div style={{ display: "flex", gap: "10px", whiteSpace: "nowrap" }}>
          <button
            className="invoice-reset-changes"
            onClick={handleResetChanges}
            style={{
              opacity: +discount?.discountValue > 0 ? 1 : 0.5,
              cursor: +discount?.discountValue > 0 ? "pointer" : "not-allowed",
            }}
            disabled={!(+discount?.discountValue > 0)}
          >
            <span>Reset</span>
          </button>
        </div>
      </div>

    </div>
  );
};

export default DistributorDiscountSelect;
